import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Route, useLocation } from 'react-router';
import { Link, Navigate, Routes } from 'react-router-dom';
import { Anchor, Container, Loader, Stack, Text, Title } from '@mantine/core';
import { AppHeader } from './components/app-header/app-header';
import ProtectedRoute from './components/protected-route';
import { SeoManager } from './components/seo/seo-manager';
import { PocketBaseService } from './services/pocketbase.service';

const AboutUs = lazy(() => import('./pages/AboutUs/about-us'));
const AGB = lazy(() => import('./pages/AGB/agb').then((module) => ({ default: module.AGB })));
const ChatPage = lazy(() => import('./pages/Chat/chat-page'));
const CheckoutSuccess = lazy(() => import('./pages/CheckoutSuccess/checkout-success'));
const ConfirmExchange = lazy(() => import('./pages/ConfirmExchange/confirm-exchange'));
const ConfirmPasswordReset = lazy(
  () => import('./pages/ConfirmPasswordReset/confirmPasswordReset')
);
const Contact = lazy(() =>
  import('./pages/Contact/contact').then((module) => ({ default: module.Contact }))
);
const Datenschutz = lazy(() =>
  import('./pages/Datenschutz/datenschutz').then((module) => ({ default: module.Datenschutz }))
);
const ExchangeDetails = lazy(() => import('./pages/ExchangeDetails/exchange-details'));
const FAQ = lazy(() => import('./pages/FAQ/faq'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword/forgotPassword'));
const Guide = lazy(() => import('./pages/Guide/guide'));
const Home = lazy(() => import('./pages/Home/home').then((module) => ({ default: module.Home })));
const Impressum = lazy(() => import('./pages/Impressum/impressum'));
const InsuranceInfo = lazy(() => import('./pages/InsuranceInfo/insurance-info'));
const Landing = lazy(() => import('./pages/Landing/landing'));
const Login = lazy(() => import('./pages/Login/login'));
const MyPosts = lazy(() => import('./pages/MyPosts/my-posts'));
const MySwaps = lazy(() => import('./pages/MySwaps/my-swaps'));
const MyProfile = lazy(() => import('./pages/MyProfile/my-profile'));
const CreateResidence = lazy(() => import('./pages/MyResidence/create-residence'));
const EditPictures = lazy(() => import('./pages/MyResidence/edit-pictures'));
const MyResidence = lazy(() => import('./pages/MyResidence/myresidence'));
const NewExchange = lazy(() => import('./pages/NewExchange/new-exchange'));
const RateExchange = lazy(() => import('./pages/RateExchange/rate-exchange'));
const Register = lazy(() => import('./pages/Register/register'));
const SharedResidenceDetails = lazy(
  () => import('./pages/SharedResidenceDetails/shared-residence-details')
);

const unauthenticatedRoutes = [
  '/',
  '/swaps',
  '/login',
  '/register',
  '/forgot-password',
  '/confirm-password-reset/',
  '/exchange/',
  '/agb',
  '/datenschutz',
  '/contact',
  '/impressum',
  '/faq',
  '/guide',
  '/insurance-info',
  '/about-us',
];
const headlessRoutes = [
  '/',
  '/login',
  '/register',
  '/forgot-password',
  '/confirm-password-reset',
  '/my-residence/create',
];

const pb = PocketBaseService.getInstance().getPocketBase();

/**
 * Sends a legacy URL to its replacement while preserving the query string, so
 * clients still running a cached bundle land on the current route.
 */
function LegacyRedirect({ to }: { to: string }) {
  const location = useLocation();

  return <Navigate to={{ pathname: to, search: location.search }} replace />;
}

function NotFound() {
  const { t } = useTranslation('seo');

  return (
    <Container size="sm" py="xl">
      <Stack align="center">
        <Title order={1}>{t('notFound.title')}</Title>
        <Text>{t('notFound.description')}</Text>
        <Anchor component={Link} to="/">
          {t('notFound.action')}
        </Anchor>
      </Stack>
    </Container>
  );
}

export function AppRoutes() {
  const location = useLocation();
  const isUnauthenticatedRoute = unauthenticatedRoutes.some((route) =>
    route.endsWith('/') ? location.pathname.startsWith(route) : location.pathname === route
  );
  const isHeadlessRoute = headlessRoutes.some((route) =>
    route === '/' ? location.pathname === '/' : location.pathname.startsWith(route)
  );
  const isLogged = pb.authStore.isValid;
  if (!isLogged) {
    pb.authStore.clear();
    if (!isUnauthenticatedRoute) {
      window.location.href = '/login';
    }
  }

  return (
    <div className="app">
      <SeoManager />
      {!isHeadlessRoute && <AppHeader />}
      <main className="main-content">
        <Suspense
          fallback={
            <Stack align="center" py="xl" aria-label="Loading">
              <Loader />
            </Stack>
          }
        >
          <Routes>
            <Route
              path="/login"
              element={
                <Login
                  onLogin={() => {
                    window.location.href = '/swaps';
                  }}
                />
              }
            />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/confirm-password-reset/:token" element={<ConfirmPasswordReset />} />
            <Route path="/" element={<Landing />} />
            <Route path="/swaps" element={<Home />} />
            <Route path="/agb" element={<AGB />} />
            <Route path="/datenschutz" element={<Datenschutz />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/guide" element={<Guide />} />
            <Route path="/insurance-info" element={<InsuranceInfo />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route
              path="/rate-exchange/:exchangeId"
              element={
                <ProtectedRoute>
                  <RateExchange />
                </ProtectedRoute>
              }
            />
            <Route
              path="/exchange/new"
              element={
                <ProtectedRoute>
                  <NewExchange />
                </ProtectedRoute>
              }
            />
            <Route path="/exchange/:exchangeId" element={<ExchangeDetails />} />
            <Route
              path="/my-residence"
              element={
                <ProtectedRoute>
                  <MyResidence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-residence/edit-pictures"
              element={
                <ProtectedRoute>
                  <EditPictures />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-residence/create"
              element={
                <ProtectedRoute>
                  <CreateResidence />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-swaps"
              element={
                <ProtectedRoute>
                  <MySwaps />
                </ProtectedRoute>
              }
            />
            {/* Legacy URL; keep until cached clients have rolled over to /my-swaps. */}
            <Route path="/my-exchanges" element={<LegacyRedirect to="/my-swaps" />} />
            <Route
              path="/chats/:chatId/shared-residence-details"
              element={
                <ProtectedRoute>
                  <SharedResidenceDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/chats/:chatId"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/confirm-exchange/:chatId"
              element={
                <ProtectedRoute>
                  <ConfirmExchange />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-profile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-posts"
              element={
                <ProtectedRoute>
                  <MyPosts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout-success/:exchangeId"
              element={
                <ProtectedRoute>
                  <CheckoutSuccess />
                </ProtectedRoute>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
